import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Divider,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Dashboard,
  CalendarMonth,
  People,
  RoomService,
  Store,
  Business,
  Schedule,
  ChevronLeft,
  BugReport,
  AccountBalance,
  ConfirmationNumber,
  AccountCircle,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const Sidebar = ({ open, onClose }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { storeId: authStoreId } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Extract store ID from current location path
  const getStoreIdFromPath = (pathname: string): string | null => {
    const match = pathname.match(/\/store\/([^\/]+)/);
    return match ? match[1] : null;
  };

  const currentStoreId = getStoreIdFromPath(location.pathname) || authStoreId;

  // Don't show store-specific menu items if we don't have a store ID
  const menuItems = currentStoreId ? [
    { text: 'Dashboard', icon: <Dashboard />, path: `/store/${currentStoreId}/dashboard` },
    { text: 'Appointments', icon: <CalendarMonth />, path: `/store/${currentStoreId}/appointments` },
    { text: 'Staff', icon: <People />, path: `/store/${currentStoreId}/staff` },
    { text: 'Services', icon: <RoomService />, path: `/store/${currentStoreId}/services` },
    { text: 'Schedule', icon: <Schedule />, path: `/store/${currentStoreId}/schedule` },
    { text: 'Store Details', icon: <Store />, path: `/store/${currentStoreId}/details` },
    { text: 'Payments', icon: <AccountBalance />, path: `/store/${currentStoreId}/payments` },
    { text: 'Manage Seats', icon: <ConfirmationNumber />, path: `/store/${currentStoreId}/seats` },
    { text: 'My Profile', icon: <AccountCircle />, path: '/my-profile' },
    { text: 'All Stores', icon: <Business />, path: '/stores' },
    { text: 'Networks', icon: <Business />, path: '/networks' },
    ...(import.meta.env.DEV ? [
      { text: 'Tests', icon: <BugReport />, path: '/test' },
    ] : []),
  ] : [
    { text: 'My Profile', icon: <AccountCircle />, path: '/my-profile' },
    { text: 'All Stores', icon: <Business />, path: '/stores' },
    { text: 'Networks', icon: <Business />, path: '/networks' },
    ...(import.meta.env.DEV ? [
      { text: 'Tests', icon: <BugReport />, path: '/test' },
    ] : []),
  ];

  const handleItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawerWidth = 280;

  const drawerContent = (
    <>
      <Box sx={{
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        minHeight: 64
      }}>
        <IconButton onClick={onClose} size="small">
          <ChevronLeft />
        </IconButton>
      </Box>

      <Divider />

      <List sx={{ flexGrow: 1, pt: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleItemClick(item.path)}
              selected={location.pathname === item.path}
              sx={{
                mx: 1,
                borderRadius: 2,
                mb: 0.5,
                minHeight: 48,
                px: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                },
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Tooltip title="" placement="right" disableHoverListener>
                <Box sx={{
                  minWidth: 40,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'inherit',
                }}>
                  {item.icon}
                </Box>
              </Tooltip>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  // Mobile: temporary drawer (overlay)
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: permanent collapsed/expanded drawer
  const desktopWidth = open ? drawerWidth : 64;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: desktopWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: desktopWidth,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
        },
      }}
    >
      <Box sx={{
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: open ? 'flex-end' : 'center',
        minHeight: 64
      }}>
        {open && (
          <IconButton onClick={onClose} size="small">
            <ChevronLeft />
          </IconButton>
        )}
      </Box>

      <Divider />

      <List sx={{ flexGrow: 1, pt: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleItemClick(item.path)}
              selected={location.pathname === item.path}
              sx={{
                mx: open ? 1 : 0.5,
                borderRadius: 2,
                mb: 0.5,
                minHeight: 48,
                justifyContent: open ? 'flex-start' : 'center',
                px: open ? 2 : 1,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                },
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Tooltip title={!open ? item.text : ''} placement="right" disableHoverListener={open}>
                <Box sx={{
                  minWidth: open ? 40 : 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'inherit',
                }}>
                  {item.icon}
                </Box>
              </Tooltip>
              {open && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: location.pathname === item.path ? 600 : 400,
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar;
